import React from 'react';
import { Row, Col, Button } from "react-bootstrap";
import { cloneDeep, get as lodashGet } from "lodash";
import moment from 'moment';
import axios from 'axios';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { EXTERNAL_API_MAP } from '../../../../constants/externalApis';
import { ExternalLink } from "../../../common/ExternalLink";
import Popover from "../../../common/Popover";
import Alert from '../../../common/Alert';


/**
 * Returns a snippet of JSX to be inserted where you need to give the user
 * information about creating labels and not including private health
 * information (PHI) in those labels.
 *
 * @param {String} label - one of Individual, Family, Group, etc.
 * @returns {JSX.Element} - note with label inserted
 */
export const renderLabelNote = (label) => {
  return (
    <span className="curation-label-note">
      Note: Do not enter real names in this field. Please enter a label to help
      you keep track of this {label} within the interface. We suggest using the
      first author's last name and then the case descriptor used in the paper
      (e.g. Smith Case 5). <b>DO NOT</b> reuse the identifier for another piece
      of evidence within the curation.
    </span>
  );
};

/**
 * Returns a snippet of JSX to be inserted where you need a note that tells the
 * user to enter phenotypes.
 *
 * @author Liam Mulhall <liammulh@gmail.com>
 * @param {String} label - one of Individual, Family, or Group
 * @returns {JSX.Element} - note with label inserted
 */
export const addPhenotypeNote = (label) => {
  return (
    <p>
      Enter <span className="serious-text">phenotypes that are NOT present
      in {label}</span> if they are specifically noted in the paper.
    </p>
  );
}

// Pull values from a list of comma-separated values that match the regular expression given in 're'.
// If resulting values should be converted to uppercase, pass true in 'uppercase'.
const getIdsFromList = (list, re, uppercase = false) => {
  if (list && list.length) {
    let newList = {};
    const rawList = list.split(',');
    if (rawList && rawList.length) {
      newList = rawList.map((item) => {
        const m = re.exec(item);
        return m ? (uppercase ? m[1].toUpperCase() : m[1]) : null;
      });
    }
    return newList;
  }
  return null;
};

/**
 * Pull values from a list of comma-separated values that do not match
 * the regular expression given in 're'.
 *
 * @param {string} list - comma-separated values
 * @param {string} re - given regular expression
 * @Returns {array} - array of invalid values
 */
const getInvalidIdsFromList = (list, re) => {
  if (list && list.length) {
    const rawList = list.split(',');
    const badList = rawList.filter(item => {
      return re.exec(item) === null;
    });
    return badList;
  }
  return null;
};

// Find all the comma-separated invalid PMID occurrences. Return all invalid PMIDs in an array.
export const getInvalidPmidsFromList = (list) => {
  return getInvalidIdsFromList(list, /^\s*([1-9]{1}\d*)\s*$/);
};

// Find all the comma-separated PMID occurrences. Return all valid PMIDs in an array.
export const getPmidsFromList = (list) => {
  return getIdsFromList(list, /^\s*([1-9]{1}\d*)\s*$/);
};

// Find all the comma-separated invalid gene-symbol occurrences. Return all invalid symbols in an array.
export const getInvalidGenesFromList = (list) => {
  return getInvalidIdsFromList(list, /^\s*([\w-]+)\s*$/, true);
};

// Find all the comma-separated gene-symbol occurrences. Return all valid symbols in an array.
export const getGenesFromList = (list) => {
  return getIdsFromList(list, /^\s*([\w-]+)\s*$/, true);
};

// Find all the comma-separated HPO ID occurrences. Return all valid HPO ID in an array.
export const getHpoIdsFromList = (list) => {
  return getIdsFromList(list, /^\s*(HP:\d{7})\s*$/i, true);
};

// Find all the comma-separated Uberon ID occurrences. Return all valid Uberon ID in an array.
export const getUberonIdsFromList = (list) => {
  return getIdsFromList(list, /^\s*((UBERON_|UBERON:)\d{7})\s*$/i, true);
};

// Find all the comma-separated HPO/MP ID occurrences. Return all valid HPO/MP ID in an array.
export const getHpoMpIdsFromList = (list) => {
  return getIdsFromList(list, /^\s*((HP:|MP:)\d{7})\s*$/i, true);
};

// Find all the comma-separated GO_Slim ID occurrences. Return all valid GO_Slim ID in an array.
export const getGoSlimIdsFromList = (list) => {
  return getIdsFromList(list, /^\s*(GO:\d{7})\s*$/i, true);
};

// Find all the comma-separated EFO ID occurrences. Return all valid EFO IDs in an array.
export const getEfoIdsFromList = (list) => {
  return getIdsFromList(list, /^\s*((EFO_|EFO:)\d{7})\s*$/i, true);
};

// Find all the comma-separated CL Ontology ID occurrences. Return all valid Uberon ID in an array.
export const getCloIdsFromList = (list) => {
  return getIdsFromList(list, /^\s*((CL_|CL:)\d{7})\s*$/i, true);
};

// Find all the comma-separated EFO/CLO ID occurrences. Return all valid EFO/CLO IDs in an array.
export const getEfoCloIdsFromList = (list) => {
  return getIdsFromList(list, /^\s*((EFO_|EFO:|CL_|CL:)\d{7})\s*$/i, true);
};

// HTML labels for inputs follow.
// not if true to show 'NOT' version of label
export const renderLabelPhenoTerms = (not) => {
  return (
    <label>
      <span>
        {not ? <span className="emphasis"><span className="serious-text">NOT Phenotype(s)</span>&nbsp;</span> : <span>Phenotype(s) in Common&nbsp;</span>}
          (<span className="normal">free text</span>):
      </span>
    </label>
  );
};

export const renderHPOList = (hpoList) => {
  // expect hpoList is array of hpo term
  return (
    <>
    {hpoList && hpoList.map((hpo, i) => {
      let id = hpo.match(/HP:\d{7}/g);
      return <span key={hpo}>{i > 0 ? ', ' : ''}<ExternalLink href={EXTERNAL_API_MAP['HPO'] + id} title={"HPO Browser entry for " + hpo + " in new tab"}>{hpo}</ExternalLink></span>;
    })}
    </>
  );
};

export const renderPMIDList = (pmidList) => {
  // expect pmidList is array of pmid
  return (
    <>
      {pmidList && pmidList.map((id, i) => {
        return <span key={i}>{i > 0 ? ', ' : ''}<ExternalLink href={EXTERNAL_API_MAP['PubMed'] + id} title={`PubMed entry for PMID:${id} in new tab`}>PMID:{id}</ExternalLink></span>;
      })}
    </>
  );
};

// Given an array of group or families in 'objList', render a list of IDs for all diseases in those
// groups or families.
export const renderDiseaseList = (objList, title) => {
  return (
    <div>
      {objList && objList.length ?
        <div>
          {objList.map(obj => {
            return (
              <Row key={obj.PK} className="form-group">
                <Col sm="5">
                  <strong className="float-right">Disease(s) Associated with {title}:</strong>
                </Col>
                <Col sm="7">
                  <strong>
                    {(obj.commonDiagnosis && obj.commonDiagnosis.length > 0) ?
                      obj.commonDiagnosis.map((disease, i) => {
                        const diseaseName = disease && disease.term && !disease.freetext
                          ? (disease.term + (disease.PK ? ` (${disease.PK})` : ''))
                          : (disease.term ? disease.term : '');
                        return (
                          <span key={disease.PK}>
                            {i > 0 ? ', ' : ''}
                            {diseaseName}
                          </span>
                        );
                      })
                      :
                      <span>&nbsp;</span>
                    }
                  </strong>
                </Col>
              </Row>
            );
          })}
        </div>
        : null
      }
    </div>
  );
};

// Given an array of group or families in 'objList', render a list of HPO IDs and/or Phenotype free text in those groups and familes.
export const renderPhenotype = (objList, title, type = '', parentObjName) => {
  return (
    <div>
      {type === 'hpo' || type === '' ? <Col sm="5">&nbsp;</Col> : null}
      {title === 'Experimental' && (type === 'hpo' || type === '') ?
        <Col sm={{ span: 7, offset: 5 }} className="alert alert-warning">
          <p style={{ 'marginBottom': '10px' }}>
            Please enter the relevant phenotypic feature(s) <strong>(required)</strong> using the Human Phenotype Ontology (HPO)
            terms wherever possible (e.g. HP:0010704, HP:0030300). If you are unable to find an appropriate HPO term, use the free text box instead.
            Please email <a href="mailto:clingen-helpdesk@lists.stanford.edu">clingen-helpdesk@lists.stanford.edu</a> for any ontology support.
          </p>
        </Col>
        : null}
      {title === 'Family' && (type === 'hpo' || type === '') ?
        <Col sm={{ span: 7, offset: 5 }}>
          <p style={{ 'marginBottom': '10px' }}>
            Please enter the relevant phenotypic feature(s) of the Family using the Human Phenotype Ontology (HPO)
            terms wherever possible (e.g. HP:0010704, HP:0030300).
            If no HPO code exists for a particular feature, please describe it in the free text box instead.
          </p>
        </Col>
        : null}
      {title === 'Individual' && (type === 'hpo' || type === '') ?
        <Col sm={{ span: 7, offset: 5 }}>
          <p style={{ 'marginBottom': '10px' }}>
            Please enter the relevant phenotypic feature(s) of the Individual using the Human Phenotype Ontology (HPO)
            terms wherever possible (e.g. HP:0010704, HP:0030300).
            If no HPO code exists for a particular feature, please describe it in the free text box instead.
          </p>
        </Col>
        : null}
      {objList && objList.length ?
        <div>
          {objList.map(obj => {
            return (
              <Row key={obj.PK} className="form-group">
                <Col sm="5">
                  {(type === 'hpo' || type === 'ft') ? <strong className="float-right">Phenotype(s) Associated with {parentObjName ? parentObjName : title}
                    {type === 'hpo' ? <span style={{ fontWeight: 'normal' }}> (<ExternalLink href={EXTERNAL_API_MAP['HPOBrowser']} title="Open HPO Browser in a new tab">HPO</ExternalLink> ID(s))</span> : null}
                    {type === 'ft' ? <span style={{ fontWeight: 'normal' }}> (free text)</span> : null}
                  :</strong> : null}
                  {(type === 'nothpo' || type === 'notft') ? <strong className="float-right">NOT Phenotype(s) Associated with {parentObjName ? parentObjName : title}
                    {type === 'nothpo' ? <span style={{ fontWeight: 'normal' }}> (<ExternalLink href={EXTERNAL_API_MAP['HPOBrowser']} title="Open HPO Browser in a new tab">HPO</ExternalLink> ID(s))</span> : null}
                    {type === 'notft' ? <span style={{ fontWeight: 'normal' }}> (free text)</span> : null}
                  :</strong> : null}
                </Col>
                <Col sm="7">
                  {(type === 'hpo' || type === '') && (obj.hpoIdInDiagnosis && obj.hpoIdInDiagnosis.length > 0) ?
                    obj.hpoIdInDiagnosis.map((hpoid, i) => {
                      return (
                        <span key={hpoid}>
                          {hpoid}
                          {i < obj.hpoIdInDiagnosis.length - 1 ? ', ' : ''}
                          {i === obj.hpoIdInDiagnosis.length - 1 && obj.termsInDiagnosis && type === '' ? '; ' : null}
                        </span>
                      );
                    })
                    : null
                  }
                  {(type === 'nothpo' || type === '') && (obj.hpoIdInElimination && obj.hpoIdInElimination.length > 0) ?
                    obj.hpoIdInElimination.map((nothpoid, i) => {
                      return (
                        <span key={nothpoid}>
                          {nothpoid}
                          {i < obj.hpoIdInElimination.length - 1 ? ', ' : ''}
                          {i === obj.hpoIdInElimination.length - 1 && obj.termsInElimination && type === '' ? '; ' : null}
                        </span>
                      );
                    })
                    : null
                  }
                  {type === 'ft' && obj.termsInDiagnosis ?
                    <span>{obj.termsInDiagnosis}</span>
                    :
                    null
                  }
                  {type === 'notft' && obj.termsInElimination ?
                    <span>{obj.termsInElimination}</span>
                    :
                    null
                  }
                </Col>
              </Row>
            );
          })}
        </div>
        : null}
    </div>
  );
};

// Render a single item of evidence data (demographics, methods, etc.) from a "parent" group or family
export const renderParentEvidence = (label, value) => {
  return (
    <>
      <Row className="form-group parent-evidence">
        <Col sm="5" className="control-label">
          <label>{label}</label>
        </Col>
        <Col sm="7">
          {value ? <span>{value}</span> : null}
        </Col>
      </Row>
    </>
  );
}

// Generate a display version (string) of the allele frequency (preferring fixed-point notation over exponential)
export const displayAlleleFrequency = (alleleFrequency) => {
  let alleleFreqDisplay = (alleleFrequency > 0 || alleleFrequency < 0) ? alleleFrequency.toFixed(5) : '0';

  // If there are no non-zero digits when using fixed-point notation (to 5 decimal places), switch to exponential
  if (alleleFreqDisplay.match(/^0+\.0+$/)) {
    alleleFreqDisplay = alleleFrequency.toExponential(2);

    // If there are more than 5 digits in the "integer part", switch to exponential
  } else if (alleleFreqDisplay.match(/^\d{6}/)) {
    alleleFreqDisplay = alleleFrequency.toExponential(2);
  }

  return alleleFreqDisplay;
}

// Generate HTML to display the allele frequency (as a fraction and a decimal number)
export const renderAlleleFrequency = (numberWithVariant, numberAllGenotypedSequenced, alleleFrequency) => {

  // Check that parameter is a non-zero number (if it's zero, only possible results of division are INF or NaN)
  if (numberAllGenotypedSequenced > 0 || numberAllGenotypedSequenced < 0) {

    // Check that parameter is a number
    if (numberWithVariant > 0 || numberWithVariant < 0 || numberWithVariant === 0) {

      // If provided allele frequency is a number, use it
      let alleleFreqDisplay = (alleleFrequency > 0 || alleleFrequency < 0 || alleleFrequency === 0)
        ? displayAlleleFrequency(Number(alleleFrequency)) : displayAlleleFrequency(numberWithVariant / numberAllGenotypedSequenced);

      return (
        <span>
          <sup>{numberWithVariant}</sup>&frasl;<sub>{numberAllGenotypedSequenced}</sub> = {alleleFreqDisplay}
        </span>
      );
    }
  }

  return;
}
export const LabelClinVarVariant = () => {
  return <span><ExternalLink href={EXTERNAL_API_MAP['ClinVar']} title="ClinVar home page at NCBI in a new tab">ClinVar</ExternalLink> Variation ID:</span>;
};

export const LabelCARVariant = () => {
  return <span><ExternalLink href={EXTERNAL_API_MAP['CAR']} title="ClinGen Allele Registry in a new tab">ClinGen Allele Registry</ExternalLink> ID:</span>;
};

export const LabelCACNVariant = () => {
  return <span><ExternalLink href={EXTERNAL_API_MAP['CAR']} title="ClinGen Allele Registry in a new tab">ClinGen Allele Registry</ExternalLink> Copy Number ID:</span>
};

/**
 * Method to render a mouseover explanation for the variant title
 */
export const renderVariantTitleExplanation = () => {
  const explanation = 'The HGVS is based on the Community Standard Title provided by the ClinGen Allele Registry, if there is no Community Standard Title, then the ClinVar Preferred Title is used. If there is no ClinVar Preferred Title, then the HGVS is based on the GRCh38 genomic coordinates.';
  return (
    <span className="variant-title-explanation">
      <Popover
        triggerComponent={<FontAwesomeIcon className="text-info" icon={faInfoCircle}/>}
        content={explanation}
        placement="top"
      />
    </span>
  );
};

export const LabelVariantTitle = (variant, linkout) => {
  const clinVarVariantTitle = <span><ExternalLink href={EXTERNAL_API_MAP['ClinVar']} title="ClinVar home page at NCBI in a new tab">ClinVar</ExternalLink> Preferred Title:</span>;
  let variantLabel = "";
  if (lodashGet(variant, "clinvarVariantTitle", null)) {
    variantLabel = linkout ? clinVarVariantTitle : 'ClinVar Preferred Title:';
  } else if (lodashGet(variant, "maneTranscriptTitle", null)) {
    variantLabel = 'MANE Transcript HGVS Title:';
  } else if (lodashGet(variant, "canonicalTranscriptTitle:", null)) {
    variantLabel = 'Canonical Transcript HGVS Title:';
  } else if (lodashGet(variant, "hgvsNames", null) && (lodashGet(variant, "hgvsNames.GRCh38", null) || lodashGet(variant, "hgvsNames.GRCh37", null))) {
    variantLabel = 'Genomic HGVS Title:';
  } else {
    variantLabel = 'Community Standard Title:';
  }

  return variantLabel;
};

export const renderVariantLabelAndTitle = (variant, linkout=false, showInHeader=false) => {
  let variantLabel = LabelVariantTitle(variant, linkout);

  if (linkout) {
    return (
      <Row>
        <Col sm="5" className="control-label"><label>{variantLabel}</label></Col>
        <Col sm="7" className={lodashGet(variant, "clinvarVariantTitle", null) ? "text-no-input clinvar-preferred-title" : "text-no-input"}>{lodashGet(variant, "preferredTitle", null)}{renderVariantTitleExplanation()}</Col>
      </Row>
    );
  } else if (showInHeader) {
    return (
      <span>
        <span className="term-name">{variantLabel}: </span>
        <span className="term-value font-weight-normal">{lodashGet(variant, "preferredTitle", null)}</span>
      </span>
    );
  } else {
    return (
      <div>
        <dt className="variant-title-row">{variantLabel}</dt>
        <dd className="variant-title-row">{lodashGet(variant, "preferredTitle", null)}</dd>
      </div>
    );
  }
};

export const renderVariantOverlappingGenes = (variant) => {
  if (variant && variant.overlappingGenes) {
    return (
      <div>
        <ul>
          {variant.overlappingGenes.map((symbol, i) => {
            return (
              <li key={i}>
                {symbol}
              </li>
            );
          })}
        </ul>
      </div>
    );
  } else {
    return (
      <span> No overlapping genes available for this Copy Number Variant</span>
    );
  }
};

export const renderNotOwnerMessage = (ownerError) => {
  const errorMessage = "<strong>You are not logged in with the same affiliation as the GDM owner so cannot do any modifications.</strong>";

  if (ownerError) {
    return (<Alert value={errorMessage} type="danger" className="mt-3"/>);
  } else {
    return null;
  }
};

/**
 * Return a snippet of JSX that show PMIDs related errors using given data
 *
 * @param {string} formError - input form error message
 * @param {array} badPmids - bad PubMed ID list
 * @param {array} preprintPmids - preprint PubMed ID list
 * @returns {JSX.Element} - PMIDs related errors
 */
export const renderPmidsErrorMessage = (formError=null, badPmids=[], preprintPmids=[]) => {
  if (formError || badPmids?.length || preprintPmids?.length) {
    const prePrintText = "Preprint is not supported, please remove.";
    return (
      <>
        {formError ? formError : null}
        {badPmids.length
          ? <div>PMID(s): {badPmids.join(', ')} cannot be found.</div>
          : null
        }
        {preprintPmids.length
          ? <span>Prepint(s): {preprintPmids.join(', ')}. {prePrintText}</span>
          : null
        }
      </>
    );
  } else {
    return null;
  }
}

/**
 * Return boolean flag if given GDM's MOI is "Semidominant" or
 * "Typified by somatic mosaicism"
 *
 * @param {object} gdm - GDM object
 * @returns {boolean} - true if MOI is matched else false
 */
export const isSemiOrSMo = (gdm) => {
  const result = gdm && gdm.modeInheritance
    ? (gdm.modeInheritance.indexOf('Semidominant') > -1
      || gdm.modeInheritance.indexOf('somatic mosaicism') > -1)
    : false;

  return result;
}

/**
 * Return boolean flag if given GDM's MOI is "Autosomal dominant"
 *
 * @param {object} gdm - GDM object
 * @returns {boolean} - true if MOI is matched else false
 */
export const isAutoDom = (gdm) => {
  const result = gdm && gdm.modeInheritance
    ? gdm.modeInheritance.indexOf('Autosomal dominant') > -1
    : false;

  return result;
}

/**
 * Return boolean flag if given GDM's MOI is "Autosomal recessive"
 *
 * @param {object} gdm - GDM object
 * @returns {boolean} - true if MOI is matched else false
 */
export const isAutoRec = (gdm) => {
  const result = gdm && gdm.modeInheritance
    ? gdm.modeInheritance.indexOf('Autosomal recessive') > -1
    : false;

  return result;
}

/**
 * Return boolean flag if given GDM's MOI is "X-linked inheritance"
 *
 * @param {object} gdm - GDM object
 * @returns {boolean} - true if MOI is matched else false
 */
export const isXLinked = (gdm) => {
  const result = gdm && gdm.modeInheritance
    ? gdm.modeInheritance.indexOf('X-linked inheritance') > -1
    : false;

  return result;
}

/**
 * Parse gnomAD population data from LDH
 * @param {object} gnomADSourcePopData - data from LHD
 *  can be Exomes/Genomes category frequency or subcohort frequency data
 *
 * Copy from variant-central/tab-content/population/Population.js
 */
const parseLDHgnomADPopData = (gnomADSourcePopData) => {
  let gnomADv4PopData = {};

  if (gnomADSourcePopData) {
    // Save allele count, number and frequency, IDs and name
    // If cohort.id is "ALL", then data is used to set Exomes/Genomes totals
    // Otherwise, data is for subcohort
    gnomADv4PopData.AC = 'focusAlleleCount' in gnomADSourcePopData ? gnomADSourcePopData.focusAlleleCount : '--';
    gnomADv4PopData.AN = 'locusAlleleCount' in gnomADSourcePopData ? gnomADSourcePopData.locusAlleleCount : '--';
    gnomADv4PopData.AF = 'alleleFrequency' in gnomADSourcePopData ? parseFloat(gnomADSourcePopData.alleleFrequency) : '--';
    gnomADv4PopData.FAFID = 'id' in gnomADSourcePopData ? gnomADSourcePopData.id : '';
    if (gnomADSourcePopData.cohort) {
      gnomADv4PopData.ID = 'id' in gnomADSourcePopData.cohort ? gnomADSourcePopData.cohort.id : gnomADv4PopData.FAFID;
      gnomADv4PopData.Name = Array.isArray(gnomADSourcePopData.cohort.characteristics) && gnomADSourcePopData.cohort.characteristics.length > 0
        && 'value' in gnomADSourcePopData.cohort.characteristics[gnomADSourcePopData.cohort.characteristics.length - 1]
        ? gnomADSourcePopData.cohort.characteristics[gnomADSourcePopData.cohort.characteristics.length - 1].value : '';
    } else {
      gnomADv4PopData.ID = '';
      gnomADv4PopData.Name = '';
    }
    // Save number of homozygotes & hemizygotes and filtering allele frequency data
    if (gnomADSourcePopData.ancillaryResults) {
      const gnomADSourcePopDataAR = gnomADSourcePopData.ancillaryResults;
      gnomADv4PopData.HOM = 'homozygotes' in gnomADSourcePopDataAR ? gnomADSourcePopDataAR.homozygotes : '--';
      gnomADv4PopData.HEM = 'hemizygotes' in gnomADSourcePopDataAR ? gnomADSourcePopDataAR.hemizygotes : '--';

      // Save Grpmax filtering allele frequency (95% confidence) data
      if (gnomADSourcePopDataAR.grpMaxFAF95) {
        gnomADv4PopData.GMFAF = {};
        gnomADv4PopData.GMFAF.ID = 'groupId' in gnomADSourcePopDataAR.grpMaxFAF95 ? gnomADSourcePopDataAR.grpMaxFAF95.groupId : '';
        gnomADv4PopData.GMFAF.CI = 'confidenceInterval' in gnomADSourcePopDataAR.grpMaxFAF95 ? gnomADSourcePopDataAR.grpMaxFAF95.confidenceInterval : '--';
        gnomADv4PopData.GMFAF.FAF = 'frequency' in gnomADSourcePopDataAR.grpMaxFAF95 ? parseFloat(gnomADSourcePopDataAR.grpMaxFAF95.frequency) : '--';
      }

      // Save joint Grpmax filtering allele frequency data
      if (gnomADSourcePopDataAR.jointGrpMaxFAF95) {
        gnomADv4PopData.JGMFAF = {};
        gnomADv4PopData.JGMFAF.ID = 'groupId' in gnomADSourcePopDataAR.jointGrpMaxFAF95 ? gnomADSourcePopDataAR.jointGrpMaxFAF95.groupId : '';
        gnomADv4PopData.JGMFAF.CI = 'confidenceInterval' in gnomADSourcePopDataAR.jointGrpMaxFAF95 ? gnomADSourcePopDataAR.jointGrpMaxFAF95.confidenceInterval : '--';
        gnomADv4PopData.JGMFAF.FAF = 'frequency' in gnomADSourcePopDataAR.jointGrpMaxFAF95 ? parseFloat(gnomADSourcePopDataAR.jointGrpMaxFAF95.frequency) : '--';
      }

      // Save joint allele count, number and frequency data
      if (gnomADSourcePopDataAR.jointFreq) {
        gnomADv4PopData.JFAF = {};
        gnomADv4PopData.JFAF.AC = 'AC' in gnomADSourcePopDataAR.jointFreq ? gnomADSourcePopDataAR.jointFreq.AC : '';
        gnomADv4PopData.JFAF.AN = 'AN' in gnomADSourcePopDataAR.jointFreq ? gnomADSourcePopDataAR.jointFreq.AN : '';
        gnomADv4PopData.JFAF.AF = 'AF' in gnomADSourcePopDataAR.jointFreq ? gnomADSourcePopDataAR.jointFreq.AF : '';
        gnomADv4PopData.JFAF.HOM = 'homozygote_count' in gnomADSourcePopDataAR.jointFreq ? gnomADSourcePopDataAR.jointFreq.homozygote_count : '';
        gnomADv4PopData.JFAF.HEM = 'hemizygote_count' in gnomADSourcePopDataAR.jointFreq ? gnomADSourcePopDataAR.jointFreq.hemizygote_count : '';
      }
    } else {
      gnomADv4PopData.HOM = '--';
      gnomADv4PopData.HEM = '--';
    }

    // Check if source data is subdivided (e.g. by genetic ancestry or sex)
    if (Array.isArray(gnomADSourcePopData.subcohortFrequency)) {
      gnomADv4PopData.subRows = [];

      // Use recursion to save subdivided data
      gnomADSourcePopData.subcohortFrequency.forEach(gnomADv4SubCohort => {
        const gnomADv4SubCohortData = parseLDHgnomADPopData(gnomADv4SubCohort);
        gnomADv4PopData.subRows.push(gnomADv4SubCohortData);

        // Check if source data is at sex subdivision (and save allele counts and homozygotes in sex totals)
        if (gnomADv4SubCohortData.Name === 'XX' || gnomADv4SubCohortData.Name === 'XY') {
          if (!gnomADv4PopData.sexTotals) {
            gnomADv4PopData.sexTotals = [];
          }
          const gnomADv4SexSubTotal = {
            'AC': Number(gnomADv4SubCohortData.AC) && gnomADv4SubCohortData.AC !== true ? Number(gnomADv4SubCohortData.AC) : 0,
            'AN': Number(gnomADv4SubCohortData.AN) && gnomADv4SubCohortData.AN !== true ? Number(gnomADv4SubCohortData.AN) : 0,
            'HOM': Number(gnomADv4SubCohortData.HOM) && gnomADv4SubCohortData.HOM !== true ? Number(gnomADv4SubCohortData.HOM) : 0,
            'HEM': Number(gnomADv4SubCohortData.HEM) && gnomADv4SubCohortData.HEM !== true ? Number(gnomADv4SubCohortData.HEM) : 0,
            'Name': gnomADv4SubCohortData.Name
          };
          gnomADv4PopData.sexTotals.push(gnomADv4SexSubTotal);
        } else if (gnomADv4SubCohortData.sexTotals) {
          // If sex totals exist, source data expected to be at genetic ancestry subdivision
          // If IDs match, set filtering allele frequency name (to genetic ancestry)
          if (gnomADv4PopData?.GMFAF?.ID === gnomADv4SubCohortData.FAFID) {
            gnomADv4PopData.GMFAF.Name = gnomADv4SubCohortData.Name;
          }

          if (gnomADv4PopData?.JGMFAF?.ID === gnomADv4SubCohortData.FAFID) {
            gnomADv4PopData.JGMFAF.Name = gnomADv4SubCohortData.Name;
          }

          // Add allele counts and homozygotes to sex totals
          if (!gnomADv4PopData.sexTotals) {
            gnomADv4PopData.sexTotals = gnomADv4SubCohortData.sexTotals;
          } else {
            for (let iSex = 0; iSex < gnomADv4SubCohortData.sexTotals.length; iSex++) {
              let iTotal = iSex;
              if (gnomADv4SubCohortData.sexTotals[iSex].Name !== gnomADv4PopData.sexTotals[iSex].Name) {
                iTotal = gnomADv4PopData.sexTotals.findIndex(sexElement =>
                  sexElement.Name === gnomADv4SubCohortData.sexTotals[iSex].Name
                );
              }

              if (iTotal >= 0) {
                gnomADv4PopData.sexTotals[iTotal].AC += gnomADv4SubCohortData.sexTotals[iSex].AC;
                gnomADv4PopData.sexTotals[iTotal].AN += gnomADv4SubCohortData.sexTotals[iSex].AN;
                gnomADv4PopData.sexTotals[iTotal].HOM += gnomADv4SubCohortData.sexTotals[iSex].HOM;
                gnomADv4PopData.sexTotals[iTotal].HEM += gnomADv4SubCohortData.sexTotals[iSex].HEM;
              } else {
                gnomADv4PopData.sexTotals.push(gnomADv4SubCohortData.sexTotals[iSex]);
              }
            }
          }
        }
      });
    }
  }

  return (gnomADv4PopData);
}

/*
 * Parse gnomAD data from LDH
 * @param {object} variant - variant object
 * @param {object} response - LDH response data
 *
 * Copy from variant-central/tab-content/population/Population.js
 */
const parseLDHgnomADData = (variant, response) => {
  // Identify the data to pull out (by LDH ID)
  const gnomADv4DatasetID = 'gnomAD4.1';
  const gnomADv4TotalsID = 'ALL';
  // Supported categories of gnomAD data from LDH (with mapping to VCI display names)
  const gnomADv4Categories = [{
    'DisplayName': 'Exomes',
    'LDHProperty': 'GnomADExomesV4.1'
  }, {
    'DisplayName': 'Genomes',
    'LDHProperty': 'GnomADGenomesV4.1'
  }];

  // Set saved data defaults
  let gnomADv4Data = {};
  let FAFTotalColumn = {
    'AC': 0,
    'ACCalc': 0,
    'AN': 0,
    'ANCalc': 0,
    'AF': 0,
    'AFCalc': 0,
    'Filter': [],
    'HOM': 0,
    'HOMCalc': 0,
    'HEM': 0,
    'HEMCalc': 0,
    'Name': 'Total',
    'fromLDH': false
  };
  let getgnomADv4Metadata = true;
  gnomADv4Data.FAF = [
    [''],
    ['Filters'],
    ['Allele Count'],
    ['Allele Number'],
    ['Allele Frequency'],
    ['Grpmax Filtering AF'],
    ['Grpmax Filtering AF Group'],
    ['Number of homozygotes'],
    ['Number of hemizygotes']
  ];

  // If has data.ld, check for GnomADExomesV4.1 and GnomADGenomesV4.1 category data
  if (response?.data?.data?.ld) {
    // Find all supported data categories
    gnomADv4Categories.forEach(gnomADv4Category => {
      if (Array.isArray(response.data.data.ld[gnomADv4Category.LDHProperty])) {
        // Looping through all elements of a category, but only expecting one
        response.data.data.ld[gnomADv4Category.LDHProperty].forEach(gnomADv4Dataset => {
          // Check data is from gnomAD4.1
          if (gnomADv4Dataset?.entContent?.gks_va_freq?.derivedFrom?.id === gnomADv4DatasetID) {
            // Save metadata (only once)
            if (getgnomADv4Metadata) {
              gnomADv4Data.ID = 'entId' in gnomADv4Dataset ? gnomADv4Dataset.entId : '';
              gnomADv4Data.displayID = gnomADv4Data.ID.match(/^chr/) ? gnomADv4Data.ID.substring(3) : gnomADv4Data.ID;
              gnomADv4Data.IRI = 'entIri' in gnomADv4Dataset ? gnomADv4Dataset.entIri : '';
              gnomADv4Data.Label = 'label' in gnomADv4Dataset.entContent.gks_va_freq.derivedFrom
                ? gnomADv4Dataset.entContent.gks_va_freq.derivedFrom.label : '';
              gnomADv4Data.Version = 'version' in gnomADv4Dataset.entContent.gks_va_freq.derivedFrom
                ? gnomADv4Dataset.entContent.gks_va_freq.derivedFrom.version : '';

              getgnomADv4Metadata = false;
            }
            // Save filtering allele frequency column name and filters
            // For filters, if "qcFilters" is empty and "noVariant" is absent, default to "Pass"
            let FAFColumn = {
              'Name': gnomADv4Category.DisplayName,
              'Filters': Array.isArray(gnomADv4Dataset.entContent.gks_va_freq?.qualityMeasures?.qcFilters)
                ? gnomADv4Dataset.entContent.gks_va_freq.qualityMeasures.qcFilters.length > 0
                  ? gnomADv4Dataset.entContent.gks_va_freq.qualityMeasures.qcFilters
                  : gnomADv4Dataset.entContent.noVariant ? 'No variant' : 'Pass'
                : gnomADv4Dataset.entContent.noVariant ? 'No variant' : ''
            };

            // Save population data
            const gnomADv4CategoryData = parseLDHgnomADPopData(gnomADv4Dataset.entContent.gks_va_freq);

            // If sex totals are present in population data, set allele frequency and ID for each
            if (Array.isArray(gnomADv4CategoryData.sexTotals)) {
              for (let sexElement of gnomADv4CategoryData.sexTotals) {
                sexElement.AF = sexElement.AC == 0 && sexElement.AN == 0
                  ? 0 : parseFloat(sexElement.AC / sexElement.AN);
                sexElement.ID = 'Total.' + sexElement.Name;
              }
            }

            // If group max is present in population data, save in filtering allele frequency column
            if (gnomADv4CategoryData.GMFAF) {
              FAFColumn.GMF = 'FAF' in gnomADv4CategoryData.GMFAF ? gnomADv4CategoryData.GMFAF.FAF : '';
              FAFColumn.GMN = 'Name' in gnomADv4CategoryData.GMFAF ? gnomADv4CategoryData.GMFAF.Name : '';
            }

            // If joint group max is present in population data (and greater than previously-saved frequency),
            // save in filtering allele frequency total column
            if (gnomADv4CategoryData.JGMFAF) {
              if (FAFTotalColumn.GMF) {
                if (Number(gnomADv4CategoryData.JGMFAF.FAF) && gnomADv4CategoryData.JGMFAF.FAF > FAFTotalColumn.GMF) {
                  FAFTotalColumn.GMF = gnomADv4CategoryData.JGMFAF.FAF;
                  FAFTotalColumn.GMN = 'Name' in gnomADv4CategoryData.JGMFAF ? gnomADv4CategoryData.JGMFAF.Name : '';
                }
              } else {
                FAFTotalColumn.GMF = 'FAF' in gnomADv4CategoryData.JGMFAF ? gnomADv4CategoryData.JGMFAF.FAF : '';
                FAFTotalColumn.GMN = 'Name' in gnomADv4CategoryData.JGMFAF ? gnomADv4CategoryData.JGMFAF.Name : '';
              }
            }
            // If joint frequency is present in population data, save in filtering allele frequency total column
            if (gnomADv4CategoryData.JFAF && !FAFTotalColumn.fromLDH) {
              FAFTotalColumn.AC = gnomADv4CategoryData.JFAF.AC;
              FAFTotalColumn.AN = gnomADv4CategoryData.JFAF.AN;
              FAFTotalColumn.AF = gnomADv4CategoryData.JFAF.AF;
              FAFTotalColumn.HOM = gnomADv4CategoryData.JFAF.HOM;
              FAFTotalColumn.HEM = gnomADv4CategoryData.JFAF.HEM;
              FAFTotalColumn.fromLDH = true;
            }

            // Save allele and homozygote & hemizygotes data as category and filtering allele frequency totals
            // For the "all" category, there's no need to sum the data
            if (gnomADv4CategoryData.ID === gnomADv4TotalsID) {
              FAFColumn.AC = gnomADv4CategoryData.AC;
              FAFColumn.AN = gnomADv4CategoryData.AN;
              FAFColumn.AF = gnomADv4CategoryData.AF;
              FAFColumn.HOM = gnomADv4CategoryData.HOM;
              FAFColumn.HEM = gnomADv4CategoryData.HEM;
            } else {
              // No "all" category then loop thru subRow to sum data
              let categoryTotals = {
                'AC': 0,
                'AN': 0,
                'AF': 0,
                'HOM': 0,
                'HEM': 0,
                'ID': 'Total',
                'Name': 'Total'
              };
              for (const categoryElement of gnomADv4CategoryData.subRows) {
                categoryTotals.AC += Number(categoryElement.AC) && categoryElement.AC !== true ? Number(categoryElement.AC) : 0;
                categoryTotals.AN += Number(categoryElement.AN) && categoryElement.AN !== true ? Number(categoryElement.AN) : 0;
                categoryTotals.HOM += Number(categoryElement.HOM) && categoryElement.HOM !== true ? Number(categoryElement.HOM) : 0;
                categoryTotals.HEM += Number(categoryElement.HEM) && categoryElement.HEM !== true ? Number(categoryElement.HEM) : 0;
              }

              categoryTotals.AF = categoryTotals.AC == 0 && categoryTotals.AN == 0
                ? 0 : parseFloat(categoryTotals.AC / categoryTotals.AN);
              gnomADv4Data[gnomADv4Category.DisplayName].TotalRows.push(categoryTotals);

              FAFColumn.AC = categoryTotals.AC;
              FAFColumn.AN = categoryTotals.AN;
              FAFColumn.AF = categoryTotals.AF;
              FAFColumn.HOM = categoryTotals.HOM;
              FAFColumn.HEM = categoryTotals.HEM;
            }
            // Add filtering allele frequency totals (for category) to display array and total column
            gnomADv4Data.FAF[0].push(FAFColumn.Name);
            gnomADv4Data.FAF[1].push(FAFColumn.Filters);
            gnomADv4Data.FAF[2].push(FAFColumn.AC);
            gnomADv4Data.FAF[3].push(FAFColumn.AN);
            gnomADv4Data.FAF[4].push(FAFColumn.AF);
            gnomADv4Data.FAF[5].push('GMF' in FAFColumn ? FAFColumn.GMF : '');
            gnomADv4Data.FAF[6].push('GMN' in FAFColumn ? FAFColumn.GMN : '');
            gnomADv4Data.FAF[7].push(FAFColumn.HOM);
            gnomADv4Data.FAF[8].push(FAFColumn.HEM);
            FAFTotalColumn.ACCalc += FAFColumn.AC;
            FAFTotalColumn.ANCalc += FAFColumn.AN;
            FAFTotalColumn.AFCalc = FAFTotalColumn.ACCalc == 0 && FAFTotalColumn.ANCalc == 0
              ? 0 : parseFloat(FAFTotalColumn.ACCalc / FAFTotalColumn.ANCalc);
            FAFTotalColumn.HOMCalc += FAFColumn.HOM;
            // If hemizygotes is not found in data then "--" so cannot add
            if (FAFColumn.HEM === "--") {
              FAFTotalColumn.HEMCalc = FAFTotalColumn.HEMCalc === 0 ?
                "--" : FAFTotalColumn.HEMCalc;
            } else {
              FAFTotalColumn.HEMCalc += FAFColumn.HEM;
            }
          }
        });
      // Data category not present, update display array and filtering allele frequency total column
      } else {
        gnomADv4Data.FAF[0].push(gnomADv4Category.DisplayName);
        gnomADv4Data.FAF[1].push('No data');
        gnomADv4Data.FAF[2].push('');
        gnomADv4Data.FAF[3].push('');
        gnomADv4Data.FAF[4].push('');
        gnomADv4Data.FAF[5].push('');
        gnomADv4Data.FAF[6].push('');
        gnomADv4Data.FAF[7].push('');
        gnomADv4Data.FAF[8].push('');

        FAFTotalColumn.Filter.push('Not in ' + gnomADv4Category.DisplayName.toLowerCase());
      }
    });

    // If no hemizygotes_count then FAFTotalColumn.HEM is "",
    // set hemizygotes count to calculated total value
    if (FAFTotalColumn.HEM === "") {
      FAFTotalColumn.HEM = FAFTotalColumn.HEMCalc;
    }

    // Add filtering allele frequency totals to display array
    gnomADv4Data.FAF[0].push(FAFTotalColumn.Name);
    gnomADv4Data.FAF[1].push(FAFTotalColumn.Filter);
    gnomADv4Data.FAF[2].push(FAFTotalColumn.AC);
    gnomADv4Data.FAF[3].push(FAFTotalColumn.AN);
    gnomADv4Data.FAF[4].push(FAFTotalColumn.AF);
    gnomADv4Data.FAF[5].push('GMF' in FAFTotalColumn ? FAFTotalColumn.GMF : '');
    gnomADv4Data.FAF[6].push('GMN' in FAFTotalColumn ? FAFTotalColumn.GMN : '');
    gnomADv4Data.FAF[7].push(FAFTotalColumn.HOM);
    gnomADv4Data.FAF[8].push(FAFTotalColumn.HEM);
  }

  // If metadata is not provided, get info for gnomAD link from variant object
  if (getgnomADv4Metadata && variant?.hgvsNames?.GRCh38) {
    const Chr = variant.hgvsNames.GRCh38.substring(7, 9);
    const Pos = variant.hgvsNames.GRCh38.match(/:g.(\d+)\D/);

    if (Chr) {
      if (Chr.indexOf('0') === 0) {
        gnomADv4Data.Chr = Chr.substring(1);
      } else if (Chr === '23') {
        gnomADv4Data.Chr = 'X';
      } else if (Chr === '24') {
        gnomADv4Data.Chr = 'Y';
      } else {
        gnomADv4Data.Chr = Chr;
      }
    }

    if (Pos?.length > 1) {
      gnomADv4Data.Pos = Pos[1];
    }
  }

  let result = cloneDeep(gnomADv4Data);
  result.source = gnomADv4DatasetID;
  result.date_created = moment().toISOString();

  // If both Exomes and Genomes have no FAF data, remove FAF
  if (gnomADv4Data.FAF[1][1] === "No data" && gnomADv4Data.FAF[1][2] === "No data") {
    delete result.FAF;
  }

  return result;
}

/**
 * Fetch variant data from LDH
 * @param {object} variant - variant object
 */
export const fetchLDHData = (variant) => {
  return new Promise((resolve) => {
    const carId = lodashGet(variant, 'carId', null);
    let result = cloneDeep(variant);

    if (carId) {
      axios.get(`https://ldh.clinicalgenome.org/ldh/Variant/id/${carId}`)
        .then(response => {
          const data = parseLDHgnomADData(variant, response);
          result.gnomADData = data;
          resolve(result);
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            resolve(result);
          }
          console.log('commonFunc.js fetchLDHData() - LDH Fetch Error=: %o', err);
          resolve(result);
        });
    } else {
      resolve(result);
    }
  });
};

